import { Add, Cancel, ChevronRight, Delete, Edit, ExpandMore, Save } from '@mui/icons-material';
import { Box, IconButton, Paper, TextField } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import {
  PRODUCT_CATEGORY_ENDPOINTS as endpointMap,
  deleteProductCategoryItem,
  fetchProductLines,
  saveProductCategoryItem,
} from '@sophocles/features/warehouse/productCategoryApi';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';

// Maps a tree-item type to the i18n label used in the delete-confirm dialog.
const CATEGORY_LABEL_KEYS = {
  'product-line': 'warehouse.product.categoryLine',
  'product-family': 'warehouse.product.categoryFamily',
  'product-sub-family': 'warehouse.product.categorySubFamily',
};

function ProductTreeView() {
  const { t } = useTranslation();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(null); // Track edit mode for a specific item
  const [expanded, setExpanded] = useState([]); // Track expanded nodes
  const [editedItem, setEditedItem] = useState({ name: '', description: '' });

  useEffect(() => {
    setLoading(true);
    fetchProductLines()
      .then(data => setData(data.results))
      .catch(error => console.error('Error fetching data:', error))
      .finally(() => setLoading(false));
  }, []);

  const handleEdit = (id, type, item) => {
    setEditMode({ id, type });
    setEditedItem({ name: item.name, description: item.description });
  };

  const handleCancel = () => {
    setEditMode(null);
    setEditedItem({ name: '', description: '' });

    // If the item was a new unsaved item, remove it from the state
    if (editMode?.id.startsWith('new')) {
      setData(prevData =>
        prevData.map(productLine => {
          if (editMode.type === 'product-family') {
            return {
              ...productLine,
              families: productLine.families.filter(family => family.id !== editMode.id),
            };
          } else if (editMode.type === 'product-sub-family') {
            return {
              ...productLine,
              families: productLine.families.map(family => ({
                ...family,
                sub_families: family.sub_families.filter(subFamily => subFamily.id !== editMode.id),
              })),
            };
          }
          return productLine;
        }),
      );
    }
  };

  const handleSave = (id, type, parentId) => {
    const payload = {
      name: editedItem.name,
      description: editedItem.description,
    };

    if (type === 'product-family') {
      payload.product_line = parentId;
    } else if (type === 'product-sub-family') {
      payload.product_family = parentId;
    }

    // If it's a new item, send POST request, otherwise PUT
    const isNew = id.startsWith('new');
    const url = isNew ? endpointMap[type] : `${endpointMap[type]}${id}/`;

    saveProductCategoryItem(url, payload, isNew)
      .then(data => {
        if (isNew) {
          // Replace the temporary item with the one returned from the server
          setData(prevData =>
            prevData.map(productLine => {
              if (type === 'product-line') {
                return productLine.id === id ? { ...productLine, ...data } : productLine;
              } else if (type === 'product-family') {
                return {
                  ...productLine,
                  families: productLine.families.map(family => (family.id === id ? { ...family, ...data } : family)),
                };
              } else if (type === 'product-sub-family') {
                return {
                  ...productLine,
                  families: productLine.families.map(family => ({
                    ...family,
                    sub_families: family.sub_families.map(subFamily =>
                      subFamily.id === id ? { ...subFamily, ...data } : subFamily,
                    ),
                  })),
                };
              }
              return productLine;
            }),
          );
        } else {
          // For existing items, just update the name/description
          setData(prevData =>
            prevData.map(productLine => {
              if (type === 'product-line' && productLine.id === id) {
                return { ...productLine, name: editedItem.name, description: editedItem.description };
              } else if (type === 'product-family') {
                return {
                  ...productLine,
                  families: productLine.families.map(family =>
                    family.id === id
                      ? { ...family, name: editedItem.name, description: editedItem.description }
                      : family,
                  ),
                };
              } else if (type === 'product-sub-family') {
                return {
                  ...productLine,
                  families: productLine.families.map(family => ({
                    ...family,
                    sub_families: family.sub_families.map(subFamily =>
                      subFamily.id === id
                        ? { ...subFamily, name: editedItem.name, description: editedItem.description }
                        : subFamily,
                    ),
                  })),
                };
              }
              return productLine;
            }),
          );
        }

        setEditMode(null);
      })
      .catch(error => console.error('Error saving data:', error));
  };

  const handleAdd = (parentId, type) => {
    const newItem = { id: `new-${Date.now()}`, name: '', description: '', families: [], sub_families: [] };
    setEditMode({ id: newItem.id, type });

    if (type === 'product-line') {
      setData(prevData => [...prevData, newItem]);
    } else if (type === 'product-family') {
      setData(prevData =>
        prevData.map(productLine =>
          productLine.id === parentId ? { ...productLine, families: [...productLine.families, newItem] } : productLine,
        ),
      );
    } else if (type === 'product-sub-family') {
      setData(prevData =>
        prevData.map(productLine =>
          productLine.families.map(family =>
            family.id === parentId ? { ...family, sub_families: [...family.sub_families, newItem] } : family,
          ),
        ),
      );
    }

    setExpanded(prevExpanded => [...prevExpanded, `line-${parentId}`]);
  };

  const handleDelete = (id, type) => {
    const url = `${endpointMap[type]}${id}/`;
    deleteProductCategoryItem(url)
      .then(() => {
        // Optionally refresh data from API or update local state
      })
      .catch(error => console.error('Error deleting data:', error));
  };

  const confirmCategoryDelete = (id, type) => {
    confirmDelete(
      { id, type },
      {
        entityLabel: t(CATEGORY_LABEL_KEYS[type]),
        onConfirm: row => handleDelete(row.id, row.type),
      },
    );
  };

  const handleToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  return (
    <AppNav loading={loading} page={`sales_product_categories`} notFullHeight={true}>
      {deleteConfirmDialog}
      <Box sx={{ top: '64px', mx: 2, pb: '24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <TitleBar showBackButton title={'Product Categories'} />
        <Paper sx={{ display: 'flex', flex: 1, mx: 1, p: 3 }}>
          <SimpleTreeView
            expandedItems={expanded}
            onExpandedItemsChange={handleToggle}
            slots={{ collapseIcon: ExpandMore, expandIcon: ChevronRight }}
            sx={{ height: 'auto', flexGrow: 1, overflowY: 'auto' }}
          >
            {data.map(productLine => (
              <TreeItem
                key={productLine.id}
                itemId={`line-${productLine.id}`}
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ flexGrow: 1 }}>{productLine.name}</span>
                    <IconButton size="small" onClick={() => handleEdit(productLine.id, 'product-line', productLine)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => confirmCategoryDelete(productLine.id, 'product-line')}>
                      <Delete />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleAdd(productLine.id, 'product-family')}>
                      <Add />
                    </IconButton>
                  </div>
                }
                onLabelClick={event => event.preventDefault()} // Prevent the label from expanding/collapsing
              >
                {productLine.families.map(family => (
                  <TreeItem
                    key={family.id}
                    itemId={`family-${family.id}`}
                    label={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ flexGrow: 1 }}>{family.name}</span>
                        <IconButton size="small" onClick={() => handleEdit(family.id, 'product-family', family)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" onClick={() => confirmCategoryDelete(family.id, 'product-family')}>
                          <Delete />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleAdd(family.id, 'product-sub-family')}>
                          <Add />
                        </IconButton>
                      </div>
                    }
                    onLabelClick={event => event.preventDefault()} // Prevent the label from expanding/collapsing
                  >
                    {family.sub_families.map(subFamily => (
                      <TreeItem
                        key={subFamily.id}
                        itemId={`sub-family-${subFamily.id}`}
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ flexGrow: 1 }}>{subFamily.name}</span>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(subFamily.id, 'product-sub-family', subFamily)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => confirmCategoryDelete(subFamily.id, 'product-sub-family')}
                            >
                              <Delete />
                            </IconButton>
                          </div>
                        }
                        onLabelClick={event => event.preventDefault()} // Prevent the label from expanding/collapsing
                      />
                    ))}
                  </TreeItem>
                ))}
              </TreeItem>
            ))}
          </SimpleTreeView>
        </Paper>
      </Box>
    </AppNav>
  );
}

export default ProductTreeView;
